import React from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import estatesService from "../../services/estatesService";

const emptyForm = {
  spaceId: "",
  title: "",
  purpose: "",
  startsAt: "",
  endsAt: "",
  attendees: 1,
  externalRequest: false,
  meetingNotice: "",
  approvalDocument: null,
  requestedServices: [],
  serviceDetails: "",
};

const dateTimeLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

const statusClass = (status) => {
  if (status === "APPROVED") return "bg-emerald-50 text-emerald-700";
  if (["REJECTED", "CANCELLED"].includes(status)) {
    return "bg-rose-50 text-rose-700";
  }
  return "bg-amber-50 text-amber-700";
};

const ResourceServiceBooking = ({ embedded = false, onBack }) => {
  const navigate = useNavigate();
  const [spaces, setSpaces] = React.useState([]);
  const [serviceCatalogue, setServiceCatalogue] = React.useState([]);
  const [bookings, setBookings] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState(null);
  const [showBookingForm, setShowBookingForm] = React.useState(false);
  const [amenitiesOpen, setAmenitiesOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [availableSpaces, ownBookings, catalogue] = await Promise.all([
        estatesService.getAvailableSpaces(),
        estatesService.getMyBookings(),
        estatesService.getServiceCatalogue(),
      ]);
      setSpaces(availableSpaces);
      setBookings(ownBookings);
      setServiceCatalogue(catalogue);
      setMessage(null);
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.message ||
          "Room booking is not available yet. Ask Estates to apply the Estates migrations.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const closeBooking = React.useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    navigate("/");
  }, [navigate, onBack]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await estatesService.createBooking({
        ...form,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        attendees: Number(form.attendees || 1),
        approvalDocument: form.approvalDocument,
        meetingNotice: form.meetingNotice,
        services: form.requestedServices,
        serviceRequests: form.requestedServices.map((code) => ({
          code,
          details: form.serviceDetails,
        })),
      });
      setForm(emptyForm);
      setShowBookingForm(false);
      setAmenitiesOpen(false);
      setMessage({
        type: "success",
        text: "Booking request submitted. Estates will review and allocate the space.",
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "Booking request could not be submitted.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className={embedded ? "space-y-4" : "min-h-screen bg-slate-50 px-5 py-8"}>
      <div className={embedded ? "space-y-4" : "mx-auto max-w-6xl space-y-6"}>
        <header className="flex items-center gap-4">
          {embedded && (
            <button
              type="button"
              onClick={closeBooking}
              className="shrink-0 whitespace-nowrap text-sm font-semibold text-blue-700 hover:underline"
            >
              ← Back to dashboard
            </button>
          )}
          <div className="min-w-0">
            <h1 className="whitespace-nowrap text-2xl font-bold text-slate-950">
              Book a space
            </h1>
            <p className="mt-1 truncate text-sm text-slate-500">
              Request an institutional room, boardroom or other bookable space.
            </p>
          </div>
        </header>

        {message && (
          <div
            className={`rounded-lg border p-4 text-sm ${message.type === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
          >
            {message.text}
          </div>
        )}

        {showBookingForm && (
          <div
            className="fixed inset-0 z-[9999] isolate flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setShowBookingForm(false);
                setAmenitiesOpen(false);
              }
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-space-booking-title"
              className="max-h-[calc(100vh-2rem)] w-full max-w-5xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl sm:p-6"
            >
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
                <div className="flex items-start gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
                    <CalendarDays className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 id="new-space-booking-title" className="text-lg font-bold text-slate-950">
                      New booking
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Choose a space and schedule. Conflicts are checked automatically.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowBookingForm(false);
                    setAmenitiesOpen(false);
                  }}
                  aria-label="Close booking form"
                  className="rounded-lg p-2 text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  ×
                </button>
              </div>
          <form
            onSubmit={submit}
            className="mt-5 grid gap-4 sm:grid-cols-2"
          >
            <label className="block text-sm font-medium text-slate-700">
              Space
              <select
                required
                value={form.spaceId}
                onChange={(event) =>
                  setForm({ ...form, spaceId: event.target.value })
                }
                className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="">
                  {loading
                    ? "Loading available spaces…"
                    : "Select a room or boardroom"}
                </option>
                {spaces.map((space) => (
                  <option key={space.id} value={space.id}>
                    {space.name} · {space.building} · capacity{" "}
                    {space.capacity}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Booking title
              <input
                required
                minLength="3"
                value={form.title}
                onChange={(event) =>
                  setForm({ ...form, title: event.target.value })
                }
                placeholder="Department meeting, seminar or class"
                className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Start
              <input
                required
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) =>
                  setForm({ ...form, startsAt: event.target.value })
                }
                className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              End
              <input
                required
                type="datetime-local"
                min={form.startsAt || undefined}
                value={form.endsAt}
                onChange={(event) =>
                  setForm({ ...form, endsAt: event.target.value })
                }
                className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Attendees
              <input
                required
                min="1"
                type="number"
                value={form.attendees}
                onChange={(event) =>
                  setForm({ ...form, attendees: event.target.value })
                }
                className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 self-end pb-3 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={form.externalRequest}
                onChange={(event) =>
                  setForm({ ...form, externalRequest: event.target.checked })
                }
              />
              External organisation
            </label>
            <fieldset className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:col-span-2">
              <legend className="px-1 text-sm font-semibold text-slate-800">
                Additional amenities and services
              </legend>
              <p className="mt-1 text-xs text-slate-500">
                Select any support needed for the booking.
              </p>
              <div className="relative mt-3">
                <button
                  type="button"
                  aria-expanded={amenitiesOpen}
                  onClick={() => setAmenitiesOpen((open) => !open)}
                  className="flex h-11 w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 text-left text-sm font-medium text-slate-700 hover:border-indigo-400"
                >
                  <span>
                    {form.requestedServices.length
                      ? `${form.requestedServices.length} selected`
                      : "Select amenities and services"}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${amenitiesOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {amenitiesOpen && (
                  <div className="absolute left-0 right-0 top-full z-[10000] mt-2 grid max-h-60 gap-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-xl sm:grid-cols-2">
                    {serviceCatalogue.map((service) => {
                      const selected = form.requestedServices.includes(
                        service.service_code,
                      );
                      return (
                        <button
                          key={service.service_code}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => {
                            setForm({
                              ...form,
                              requestedServices: selected
                                ? form.requestedServices.filter(
                                    (code) => code !== service.service_code,
                                  )
                                : [
                                    ...form.requestedServices,
                                    service.service_code,
                                  ],
                            });
                            setAmenitiesOpen(false);
                          }}
                          className="flex items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-indigo-50"
                        >
                          <span
                            className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border text-[11px] ${selected ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300"}`}
                          >
                            {selected ? "✓" : ""}
                          </span>
                          <span className="min-w-0">
                            <span className="block font-semibold">
                              {service.service_name}
                            </span>
                            <span className="block text-xs text-slate-500">
                              {service.service_department}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                    {!serviceCatalogue.length && (
                      <p className="p-2 text-sm text-slate-500 sm:col-span-2">
                        No additional services are available.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {form.requestedServices.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.requestedServices.map((code) => {
                    const service = serviceCatalogue.find(
                      (item) => item.service_code === code,
                    );
                    return (
                      <span
                        key={code}
                        className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
                      >
                        {service?.service_name || code}
                      </span>
                    );
                  })}
                </div>
              )}
              <textarea
                rows="2"
                value={form.serviceDetails}
                onChange={(event) => setForm({ ...form, serviceDetails: event.target.value })}
                placeholder="Service details, quantities or setup instructions"
                className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </fieldset>
            <label className="block text-sm font-medium text-slate-700">
              Purpose / department details
              <textarea
                rows="2"
                value={form.purpose}
                onChange={(event) =>
                  setForm({ ...form, purpose: event.target.value })
                }
                placeholder="Purpose, department and setup requirements"
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Notice to meeting / approval details
              <textarea
                required
                minLength="3"
                rows="2"
                value={form.meetingNotice}
                onChange={(event) => setForm({ ...form, meetingNotice: event.target.value })}
                placeholder="Meeting notice, approving office and any instructions"
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Approval document
              <input
                required
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(event) => setForm({ ...form, approvalDocument: event.target.files?.[0] || null })}
                className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">PDF, JPG or PNG · maximum 5 MB</span>
            </label>
            <div className="flex items-end justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBookingForm(false)}
                className="rounded-lg border bg-white px-4 py-2.5 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
            </section>
          </div>
        )}

        <section className="rounded-2xl border bg-white p-5 shadow-sm sm:p-7">
          <div className="flex flex-nowrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="whitespace-nowrap text-lg font-bold text-slate-950">My bookings</h2>
              <p className="mt-1 truncate text-sm text-slate-500">
                Track pending requests and confirmed allocations.
              </p>
            </div>
            <div className="flex shrink-0 flex-nowrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowBookingForm(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
              >
                <span className="text-lg leading-none">+</span>
                New booking
              </button>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 rounded-lg border bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
                {bookings.length} requests
              </span>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto rounded-xl border">
            <table className="min-w-[850px] w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-12 px-4 py-3">#</th>
                  <th className="px-4 py-3">Request</th>
                  <th className="px-4 py-3">Space</th>
                  <th className="px-4 py-3">Schedule</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {bookings.map((booking, index) => (
                  <tr key={booking.id}>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-400">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">
                        {booking.title}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {booking.booking_number}
                      </p>
                      {booking.purpose && (
                        <p className="mt-2 max-w-xs text-xs text-slate-600">
                          {booking.purpose}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {booking.space?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {dateTimeLabel(booking.starts_at)}
                      <br />→ {dateTimeLabel(booking.ends_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(booking.status)}`}
                      >
                        {booking.status === "APPROVED" && (
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                        )}
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!bookings.length && !loading && (
              <p className="p-8 text-center text-sm text-slate-500">
                You have not submitted a space booking yet.
              </p>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default ResourceServiceBooking;
